/**
 * Top-level views of a rule — the SAME tabs for every flavour. A chat-authored
 * rule answers its recommendation settings in the Atlas conversation and a
 * template rule on its create panel, but once the rule exists both edit them
 * on the Display settings tab: a merchant cannot tell from the URL which
 * flavour a rule is and must find the settings in one place.
 *
 * Advanced is the expert view, shown only to orgs with
 * `advanced_agentic_logic_enabled` (see {@link visibleRuleDetailTabs}). There
 * is no Versions tab: edits autosave to a draft and Discard returns to the
 * live version, so an old `?tab=versions` link lands on the default tab.
 */
export const RULE_DETAIL_TABS = [
  { key: "selection_logic", label: "Selection logic" },
  { key: "display_settings", label: "Display settings" },
  { key: "advanced", label: "Advanced" },
] as const;

export type RuleDetailTabKey = (typeof RULE_DETAIL_TABS)[number]["key"];

/**
 * Opening a rule lands on the readable Selection logic graph. Explicit tab
 * links still override this default, so expert links can open Advanced.
 */
export const DEFAULT_RULE_DETAIL_TAB: RuleDetailTabKey = "selection_logic";

/**
 * A template rule opens on the canvas instead. Its Advanced tab is raw config
 * JSON — deliberately the expert view — so the readable graph is the right
 * landing surface, and the rule is already complete by the time it exists.
 */
export const DEFAULT_TEMPLATE_RULE_DETAIL_TAB: RuleDetailTabKey =
  "selection_logic";

/**
 * The selected tab, carried in the URL so a link can land on one — Atlas sends
 * the author to the Selection logic canvas after it builds a rule — and so a
 * reload keeps the reader where they were.
 */
export const RULE_DETAIL_TAB_PARAM = "tab";

/** The tabs only an org with `advanced_agentic_logic_enabled` sees. */
const ADVANCED_ONLY_TABS: readonly RuleDetailTabKey[] = ["advanced"];

/** Whether an org with this Advanced verdict can see the tab. */
export const isRuleDetailTabVisible = (
  tab: RuleDetailTabKey,
  advancedEnabled: boolean
) => advancedEnabled || !ADVANCED_ONLY_TABS.includes(tab);

/**
 * The tabs an org actually sees. Advanced (the raw rule code or
 * configuration) is withheld unless the org's `advanced_agentic_logic_enabled`
 * toggle is on.
 */
export const visibleRuleDetailTabs = (advancedEnabled: boolean) =>
  RULE_DETAIL_TABS.filter((tab) =>
    isRuleDetailTabVisible(tab.key, advancedEnabled)
  );

/** Whether a URL value names a tab of the rule page. */
export const isRuleDetailTabKey = (
  value: string | null | undefined
): value is RuleDetailTabKey =>
  RULE_DETAIL_TABS.some((tab) => tab.key === value);
